import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {ConfigProps, Form, InjectedFormProps, reduxForm} from 'redux-form';
import {DEFAULT_FORM_NAME, roleUser} from '../constants';
import SimpleFormToolbar from './SimpleFormToolbar';
import {withTranslation} from 'react-i18next';
import {NotificationManager} from 'react-notifications';
import {TFunction} from 'i18next';
import hasPermissions from '../functions/permissions/hasPermissions';

interface FormComponentProps {
    form: string;
    save?: Function;
    afterSubmit?: Function;
    afterDelete?: Function;
    handleDelete?: (id: number | string) => void;
    className?: string;
    paperClassName?: string;
    saveButtonLabel?: string;
    saveButtonIcon?: React.ComponentType<any>;
    submitOnEnter?: boolean;
    redirect?: string | null | boolean;
    id?: string | number;
    style?: object;
    toolbar?: React.ReactElement;
    resource: string;
    loading?: boolean;
    record?: Record<string, any>;
}

type EnhancedSimpleFormProps = ConfigProps & FormComponentProps;

interface SimpleFormProps extends InjectedFormProps, FormComponentProps {
    t: TFunction;
}

const mapStateToProps = (_state: object, props: SimpleFormProps): object => {
    const id = props.record && props.record.id || props.id;
    return ({
        initialValues: {...props.initialValues, ...props.record},
        form: props.form || `${DEFAULT_FORM_NAME}-${props.resource}-${id}`,
    });
};

const wrapAction = (action?: Function, postAction?: Function) => {
    return (...data: Array<any>): void => {
        action && typeof action === 'function' && action(...data);
        postAction && typeof postAction === 'function' && postAction();
    };
};

const SimpleForm: React.FunctionComponent<SimpleFormProps> = (props) => {
    const {
        afterSubmit,
        afterDelete,
        // @ts-ignore
        toolbar = <SimpleFormToolbar />,
        paperClassName = 'detail-content-paper',
        record = {},
        children,
        className,
        handleSubmit,
        save,
        resource,
        handleDelete,
        style,
        loading,
        saveButtonLabel,
        saveButtonIcon,
        submitOnEnter = true,
        redirect,
    } = props;
    const wrappedSave = React.useMemo(() => wrapAction(save, afterSubmit), [save, afterSubmit]);
    const wrappedDelete = React.useMemo(() => wrapAction(handleDelete, afterDelete), [handleDelete, afterDelete]);
    const saveWithRedirect = React.useMemo(() =>
            (values: Record<string, any>): void =>
                wrappedSave(values, redirect),
        [wrappedSave, redirect],
    );

    const handleSubmitWithRedirect = React.useMemo(() => handleSubmit(saveWithRedirect), [saveWithRedirect]);

    return (
        <div className={paperClassName} style={style}>
            <Form
                className={className}
                onSubmit={(): void => undefined}
            >
                <React.Fragment>
                    {children}
                </React.Fragment>
                {React.cloneElement(toolbar, {
                    handleDelete: wrappedDelete,
                    handleSubmit,
                    handleSubmitWithRedirect,
                    resource,
                    record,
                    saveButtonLabel,
                    saveButtonIcon,
                    loading,
                    submitOnEnter,
                })}
            </Form>
        </div>
    );
};

// @ts-ignore
const EnhancedForm: React.FunctionComponent<EnhancedSimpleFormProps> = compose(
    connect(mapStateToProps, {}),
    // To inject translate in redux form to use it onSubmitFail
    withTranslation(),
    reduxForm({
        destroyOnUnmount: true,
        enableReinitialize: true,
        keepDirtyOnReinitialize: true,
        onSubmitFail: (_errors, _dispatch, _submitError, props: SimpleFormProps) => {
            if (_errors && !hasPermissions(roleUser)) {
                Object.keys(_errors).forEach(key => {
                    NotificationManager.error(
                        // @ts-ignore
                        JSON.stringify(_errors[key]),
                        key,
                        3500,
                    );
                });
            } else {
                NotificationManager.error(
                    props.t('common.notifications.form_invalid'),
                    props.t('common.notifications.error_title'),
                    3500,
                );
            }
        },
    }),
)(SimpleForm);

EnhancedForm.displayName = 'simple form';

export default EnhancedForm;
